import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Loader2 } from "lucide-react";

import InfiniteScroll from "@/modern/components/infinite-scroll";
import { useMediaQuery } from "@/modern/hooks/useMediaQuery";
import { usePostMountFlush } from "@/modern/hooks/usePostMountFlush";
import { cn } from "@/modern/utils";

import type { Notification, NotificationConfigContext } from "../types";
import {
  mosaicGridHeight,
  mosaicPlacementStyle,
  placeMosaicLayouts,
  MOSAIC_ROW_UNIT_PX,
} from "../utils/mosaicPlacement";
import {
  NotificationTile,
  type NotificationMosaicLayout,
} from "./NotificationTile";

const MOSAIC_GRID_CLS =
  "grid grid-cols-4 auto-rows-[minmax(11rem,auto)] gap-3 sm:grid-cols-8";

const MOSAIC_SPAN: Record<NotificationMosaicLayout, string> = {
  hero: "col-span-4 row-span-2 min-h-[11rem]",
  half: "col-span-4 min-h-[11rem]",
  cell: "col-span-2 min-h-[11rem]",
};

/** Matches Tailwind `sm:` (`min-width: 640px`). */
const SM_MQ = "(min-width: 640px)";
const OVERSCAN_PX = MOSAIC_ROW_UNIT_PX * 3;

/** Decorative skeleton pattern only — not used for real inbox rows. */
function skeletonMosaicLayout(index: number): NotificationMosaicLayout {
  if (index === 0) return "hero";
  if (index === 1 || index === 2 || index === 3) return "half";
  return "cell";
}

/**
 * Relative size tiers from loaded-list recipient_count min/max.
 * Preserves input order; does not sort. Tied counts → all cell.
 */
export function mosaicLayoutsFromRecipientCounts(
  counts: number[],
): NotificationMosaicLayout[] {
  if (counts.length === 0) return [];

  const normalized = counts.map((c) => Math.max(1, c ?? 1));
  let minC = normalized[0];
  let maxC = normalized[0];
  for (let i = 1; i < normalized.length; i++) {
    const c = normalized[i];
    if (c < minC) minC = c;
    if (c > maxC) maxC = c;
  }

  if (maxC === minC) {
    return normalized.map(() => "cell");
  }

  const range = maxC - minC;
  return normalized.map((count) => {
    const span = (count - minC) / range;
    if (span === 1) return "hero";
    if (span >= 0.5) return "half";
    return "cell";
  });
}

function SkeletonTile({
  span,
  delay,
}: {
  span: string;
  delay: number;
}) {
  return (
    <div
      className={cn("animate-pulse rounded-[1.5rem] bg-muted/50", span)}
      style={{ animationDelay: `${delay}ms` }}
    />
  );
}

export function NotificationObjectMosaicSkeleton() {
  return (
    <div className={MOSAIC_GRID_CLS}>
      {Array.from({ length: 9 }).map((_, index) => (
        <SkeletonTile
          key={index}
          span={MOSAIC_SPAN[skeletonMosaicLayout(index)]}
          delay={index * 80}
        />
      ))}
    </div>
  );
}

export function NotificationObjectMosaic({
  notifications,
  context,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onFetchNextPage,
  empty,
}: {
  notifications: Notification[];
  context: NotificationConfigContext;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onFetchNextPage: () => void;
  empty: ReactNode;
}) {
  usePostMountFlush();

  const isSmUp = useMediaQuery(SM_MQ);
  const columns: 4 | 8 = isSmUp ? 8 : 4;
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  const [gridWidth, setGridWidth] = useState(0);
  const [viewport, setViewport] = useState({ top: 0, height: 0 });

  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    setScrollRoot(node);
  }, []);

  useEffect(() => {
    if (!gridEl) return;
    const measure = () => setGridWidth(gridEl.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(gridEl);
    return () => observer.disconnect();
  }, [gridEl]);

  useEffect(() => {
    if (!scrollRoot) return;
    const sync = () => {
      setViewport({
        top: scrollRoot.scrollTop,
        height: scrollRoot.clientHeight,
      });
    };
    sync();
    scrollRoot.addEventListener("scroll", sync, { passive: true });
    const observer = new ResizeObserver(sync);
    observer.observe(scrollRoot);
    return () => {
      scrollRoot.removeEventListener("scroll", sync);
      observer.disconnect();
    };
  }, [scrollRoot]);

  const layouts = useMemo(
    () =>
      mosaicLayoutsFromRecipientCounts(
        notifications.map((n) => n.recipient_count ?? 1),
      ),
    [notifications],
  );

  const { placements, rowCount } = useMemo(
    () => placeMosaicLayouts(layouts, columns),
    [layouts, columns],
  );

  const totalHeight = mosaicGridHeight(rowCount);

  const visiblePlacements = useMemo(() => {
    const viewTop = viewport.top - OVERSCAN_PX;
    const viewBottom =
      viewport.top + Math.max(viewport.height, OVERSCAN_PX) + OVERSCAN_PX;
    // Before measure, paint a first page so the list is never blank.
    if (gridWidth <= 0) return placements.slice(0, 24);
    return placements.filter((placement) => {
      const box = mosaicPlacementStyle(placement, columns, gridWidth);
      return box.top + box.height > viewTop && box.top < viewBottom;
    });
  }, [placements, columns, gridWidth, viewport]);

  const layoutWidth = gridWidth > 0 ? gridWidth : 800;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
      >
        <div className="px-5 pb-4 pt-3 sm:px-7">
          {isLoading && notifications.length === 0 ? (
            <NotificationObjectMosaicSkeleton />
          ) : notifications.length === 0 ? (
            empty
          ) : (
            <InfiniteScroll
              isLoading={isFetchingNextPage}
              hasMore={!!hasNextPage}
              next={onFetchNextPage}
              root={scrollRoot}
              rootMargin="0px 0px 200px 0px"
            >
              <div ref={setGridEl} className="relative w-full" style={{ height: totalHeight }}>
                {visiblePlacements.map((placement) => {
                  const notification = notifications[placement.index];
                  if (!notification) return null;
                  const layout = layouts[placement.index] ?? "cell";
                  const box = mosaicPlacementStyle(placement, columns, layoutWidth);
                  return (
                    <div
                      key={notification.id}
                      className="absolute"
                      style={{
                        top: box.top,
                        left: box.left,
                        width: box.width,
                        height: box.height,
                      }}
                    >
                      <NotificationTile
                        notification={notification}
                        context={context}
                        layout={layout}
                        className="h-full min-h-0"
                      />
                    </div>
                  );
                })}
              </div>
              {isFetchingNextPage ? (
                <div className="flex justify-center pb-1 pt-4">
                  <Loader2
                    className="size-4 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                </div>
              ) : (
                <div className="h-1" />
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
